import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bar } from '../components/ui';
import { checkApprox } from './calc';
import { elapsed, type DrillOutcome } from './meta';

/** 숫자 하나를 답하는 문제 (수열 추리·기호 연산·가게 계산 공통) */
export interface NumberQuestion {
  answer: number;
  explain: string;
  limitSec: number;
  /** 답 단위 (있으면 입력칸 옆에 표시하고 정답을 쉼표로 끊어 보여 준다) */
  unit?: string;
  /** 허용 오차 — 비율(0.05 = ±5%)과 절대값 중 큰 쪽. 없으면 정확히 */
  tol?: number;
  tolAbs?: number;
  /** 음수 답이 가능 (− 입력) */
  signed?: boolean;
  /** 풀이 단계 (있으면 explain 대신 번호 목록으로) */
  steps?: string[];
}

interface Answered { ok: boolean; given: string; ms: number; info: Record<string, unknown> }

/** 문제마다 제한 시간 → 숫자 입력 → 맞으면 규칙을 잠깐 보여 주고 넘어가고, 틀리면(풀이 단계가 있으면 맞아도) '다음'을 누를 때까지 설명을 읽게 둔다 */
export function NumberDrill<Q extends NumberQuestion>({ questions, seed, onDone, className, render, info }: {
  questions: Q[];
  seed: number;
  onDone: (o: DrillOutcome) => void;
  className: string;
  /** 문제 화면. revealed = 답을 냈음 (빈칸에 정답을 보여 줄 때) */
  render: (q: Q, revealed: boolean) => ReactNode;
  /** 틀린 문제 기록에 남길 내용 */
  info: (q: Q) => Record<string, unknown>;
}) {
  const started = useRef(performance.now());
  const asked = useRef(performance.now());
  const log = useRef<Answered[]>([]);
  const [index, setIndex] = useState(0);
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState<{ ok: boolean } | null>(null);
  const [left, setLeft] = useState(1);
  const q = questions[index];
  const shown = q.unit ? `${q.answer.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}${q.unit}` : String(q.answer);

  const next = () => {
    setFeedback(null);
    setInput('');
    if (index + 1 >= questions.length) {
      const all = log.current;
      const times = all.map((a) => a.ms).sort((a, b) => a - b);
      onDone({
        correct: all.filter((a) => a.ok).length,
        total: all.length,
        duration_ms: elapsed(started.current),
        detail: { seed, median_ms: times[Math.floor(times.length / 2)], wrong: all.filter((a) => !a.ok).map((a) => ({ ...a.info, given: a.given })) },
      });
      return;
    }
    asked.current = performance.now();
    setLeft(1);
    setIndex(index + 1);
  };

  const submit = (given: string | null) => {
    if (feedback) return;
    const ok = given !== null && checkApprox(given, q.answer, q.tol, q.tolAbs);
    log.current.push({ ok, given: given ?? '시간 초과', ms: elapsed(asked.current), info: info(q) });
    setFeedback({ ok });
    if (ok && !q.steps) window.setTimeout(next, 1300); // 풀이 단계가 있는 문제는 맞혀도 읽고 넘어가게
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (feedback) return undefined;
    const id = window.setInterval(() => {
      const remain = 1 - (performance.now() - asked.current) / (q.limitSec * 1000);
      setLeft(Math.max(0, remain));
      if (remain <= 0) submitRef.current(null);
    }, 100);
    return () => window.clearInterval(id);
  }, [index, feedback, q.limitSec]);

  return (
    <div className={`drill ${className} ${feedback ? (feedback.ok ? 'ok' : 'miss') : ''}`}>
      <div className="row-between">
        <span className="drill-progress">{index + 1} / {questions.length}</span>
        <span className="small muted">{Math.ceil(left * q.limitSec)}초</span>
      </div>
      <Bar value={left} tone={left < 0.3 ? 'warn' : 'accent'} />
      {render(q, !!feedback)}
      <form className="calc-form" onSubmit={(e) => { e.preventDefault(); submit(input); }}>
        <input
          key={index} className="input calc-input" inputMode={q.signed ? 'text' : 'decimal'} autoComplete="off" autoFocus aria-label="답"
          value={input} onChange={(e) => setInput(e.target.value)} disabled={!!feedback} enterKeyHint="done" placeholder={q.signed ? '예: 25 또는 -25' : undefined}
        />
        {q.unit && <span className="calc-unit">{q.unit}</span>}
        <button type="submit" className="btn btn-primary" disabled={!!feedback || input.trim() === ''}>확인</button>
      </form>
      <div aria-live="polite">
        {feedback && (
          <>
            <p className="rule-feedback">{feedback.ok ? (q.unit ? `정답! (${shown})` : '정답!') : `정답은 ${shown}`}</p>
            {q.steps ? <ol className="biz-steps">{q.steps.map((s) => <li key={s}>{s}</li>)}</ol> : <p className="seq-explain">{q.explain}</p>}
            {(!feedback.ok || q.steps) && (
              <div className="drill-end">
                <button type="button" className="btn btn-primary" autoFocus onClick={next}>다음</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
